// ============================================
// HARDENED API CLIENT
// Retry logic, request deduplication, offline queue,
// request/response interceptors, type-safe endpoints
// ============================================

const API_BASE = import.meta.env.VITE_API_URL || 'https://luco1-cloudflare.your-subdomain.workers.dev';

// ---- Types ----

export interface ApiError {
  error: string;
  status: number;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

export interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  retryableStatuses: number[];
}

export interface RequestConfig {
  retries?: Partial<RetryConfig>;
  timeout?: number;
  skipAuth?: boolean;
  signal?: AbortSignal;
}

// ---- Retry Logic ----

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelay: 300,
  maxDelay: 5000,
  retryableStatuses: [408, 429, 500, 502, 503, 504],
};

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function getRetryDelay(attempt: number, config: RetryConfig): number {
  const exponential = config.baseDelay * Math.pow(2, attempt);
  const jitter = Math.random() * 200;
  return Math.min(exponential + jitter, config.maxDelay);
}

// ---- Request Deduplication ----

class RequestDeduplicator {
  private inFlight = new Map<string, Promise<unknown>>();

  async dedupe<T>(key: string, request: () => Promise<T>): Promise<T> {
    if (this.inFlight.has(key)) {
      return this.inFlight.get(key)! as T;
    }

    const promise = request().finally(() => {
      this.inFlight.delete(key);
    });

    this.inFlight.set(key, promise);
    return promise;
  }

  clear() {
    this.inFlight.clear();
  }
}

// ---- Offline Queue ----

interface QueuedRequest {
  id: string;
  method: string;
  path: string;
  body?: unknown;
  timestamp: number;
  retryCount: number;
}

class OfflineQueue {
  private queue: QueuedRequest[] = [];
  private storageKey = 'api_offline_queue';
  private maxSize = 50;

  constructor() {
    this.load();
  }

  private load() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        this.queue = JSON.parse(stored);
      }
    } catch {
      this.queue = [];
    }
  }

  private save() {
    localStorage.setItem(this.storageKey, JSON.stringify(this.queue));
  }

  enqueue(method: string, path: string, body?: unknown): string {
    if (this.queue.length >= this.maxSize) {
      this.queue.shift(); // Remove oldest
    }

    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.queue.push({ id, method, path, body, timestamp: Date.now(), retryCount: 0 });
    this.save();
    return id;
  }

  dequeue(): QueuedRequest | undefined {
    const item = this.queue.shift();
    this.save();
    return item;
  }

  peek(): QueuedRequest | undefined {
    return this.queue[0];
  }

  get length() {
    return this.queue.length;
  }

  clear() {
    this.queue = [];
    this.save();
  }
}

// ---- Interceptors ----

type RequestInterceptor = (config: { method: string; path: string; headers: Record<string, string>; body?: unknown }) => { method: string; path: string; headers: Record<string, string>; body?: unknown };
type ResponseInterceptor = <T>(response: ApiResponse<T>) => ApiResponse<T>;
type ErrorInterceptor = (error: ApiError) => ApiError | Promise<ApiError>;

// ---- Main API Client ----

class ApiClient {
  private token: string | null = null;
  private deduplicator = new RequestDeduplicator();
  private offlineQueue = new OfflineQueue();
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  private errorInterceptors: ErrorInterceptor[] = [];
  private isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

  constructor() {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      this.token = localStorage.getItem('auth_token');
      
      window.addEventListener('online', () => {
        this.isOnline = true;
        this.processOfflineQueue();
      });
      
      window.addEventListener('offline', () => {
        this.isOnline = false;
      });
    }
  }

  // ---- Token Management ----

  setToken(token: string) {
    this.token = token;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('auth_token', token);
    }
  }

  clearToken() {
    this.token = null;
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('auth_token');
    }
  }

  getToken(): string | null {
    if (!this.token) {
      if (typeof localStorage !== 'undefined') {
        this.token = localStorage.getItem('auth_token');
      }
    }
    return this.token;
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // ---- Interceptors ----

  addRequestInterceptor(interceptor: RequestInterceptor) {
    this.requestInterceptors.push(interceptor);
  }

  addResponseInterceptor(interceptor: ResponseInterceptor) {
    this.responseInterceptors.push(interceptor);
  }

  addErrorInterceptor(interceptor: ErrorInterceptor) {
    this.errorInterceptors.push(interceptor);
  }

  // ---- Core Request ----

  private async requestWithRetry<T>(
    method: string,
    path: string,
    body?: unknown,
    config: RequestConfig = {}
  ): Promise<T> {
    const retryConfig = { ...DEFAULT_RETRY_CONFIG, ...config.retries };
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
      try {
        return await this.executeRequest<T>(method, path, body, config);
      } catch (err: unknown) {
        lastError = err as Error;

        // Check if retryable
        const status = (err as { status?: number }).status || 0;
        const isRetryable = retryConfig.retryableStatuses.includes(status);
        
        if (!isRetryable || attempt >= retryConfig.maxRetries) {
          throw err;
        }

        // Wait before retry
        const delay = getRetryDelay(attempt, retryConfig);
        await sleep(delay);
      }
    }

    throw lastError;
  }

  private async executeRequest<T>(
    method: string,
    path: string,
    body?: unknown,
    config: RequestConfig = {}
  ): Promise<T> {
    // Build headers
    let headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    // Add auth token
    if (!config.skipAuth) {
      const token = this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    // Apply request interceptors
    let requestConfig: { method: string; path: string; headers: Record<string, string>; body?: unknown } = { method, path, headers, body };
    for (const interceptor of this.requestInterceptors) {
      requestConfig = interceptor(requestConfig);
    }

    // Check offline for mutating requests
    const isMutating = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);
    if (isMutating && !this.isOnline) {
      const queueId = this.offlineQueue.enqueue(method, path, body);
      throw { error: 'Offline: Request queued', status: 0, queueId };
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeout = config.timeout || 30000;
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    if (config.signal) {
      config.signal.addEventListener('abort', () => controller.abort());
    }

    try {
      const response = await fetch(`${API_BASE}${requestConfig.path}`, {
        method: requestConfig.method,
        headers: requestConfig.headers,
        body: requestConfig.body ? JSON.stringify(requestConfig.body) : undefined,
        credentials: 'include',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle 401 - Token expired
      if (response.status === 401) {
        this.clearToken();
        window.dispatchEvent(new CustomEvent('auth:expired'));
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
        const apiError: ApiError = {
          error: errorData.error || `HTTP ${response.status}`,
          status: response.status,
        };

        // Apply error interceptors
        let finalError: ApiError | Promise<ApiError> = apiError;
        for (const interceptor of this.errorInterceptors) {
          finalError = interceptor(await finalError);
        }
        throw await finalError;
      }

      const data = await response.json();

      // Apply response interceptors
      let apiResponse: ApiResponse<T> = {
        data,
        status: response.status,
        headers: response.headers,
      };
      for (const interceptor of this.responseInterceptors) {
        apiResponse = interceptor(apiResponse);
      }

      return apiResponse.data;

    } catch (err: unknown) {
      clearTimeout(timeoutId);

      if ((err as { name?: string }).name === 'AbortError') {
        throw { error: 'Request timeout', status: 408 };
      }

      throw err;
    }
  }

  // ---- Public Request Methods ----

  async get<T>(path: string, config?: RequestConfig): Promise<T> {
    const dedupeKey = `GET:${path}`;
    return this.deduplicator.dedupe(dedupeKey, () =>
      this.requestWithRetry<T>('GET', path, undefined, config)
    );
  }

  async post<T>(path: string, body?: unknown, config?: RequestConfig): Promise<T> {
    return this.requestWithRetry<T>('POST', path, body, config);
  }

  async put<T>(path: string, body?: unknown, config?: RequestConfig): Promise<T> {
    return this.requestWithRetry<T>('PUT', path, body, config);
  }

  async delete<T>(path: string, config?: RequestConfig): Promise<T> {
    return this.requestWithRetry<T>('DELETE', path, undefined, config);
  }

  // ---- Offline Queue Processing ----

  private async processOfflineQueue() {
    while (this.offlineQueue.length > 0 && this.isOnline) {
      const request = this.offlineQueue.peek();
      if (!request) break;

      try {
        await this.executeRequest(request.method, request.path, request.body);
        this.offlineQueue.dequeue();
      } catch (err) {
        // If it fails again, stop processing
        console.error('[API] Offline queue processing failed:', err);
        break;
      }
    }
  }

  getOfflineQueueLength(): number {
    return this.offlineQueue.length;
  }

  clearOfflineQueue() {
    this.offlineQueue.clear();
  }

  // ---- Typed API Methods ----

  // Auth
  register(displayName: string, email: string, password: string) {
    return this.post<{ token: string; player: unknown }>('/api/auth/register', {
      display_name: displayName,
      email,
      password,
    });
  }

  login(email: string, password: string) {
    return this.post<{ token: string; player: unknown }>('/api/auth/login', {
      email,
      password,
    });
  }

  logout() {
    return this.post('/api/auth/logout');
  }

  me() {
    return this.get<unknown>('/api/auth/me');
  }

  // Players
  getPlayer(id: number) {
    return this.get<unknown>(`/api/players/${id}`);
  }

  updatePlayer(id: number, updates: Record<string, unknown>) {
    return this.put(`/api/players/${id}`, updates);
  }

  getPlayerStats(id: number) {
    return this.get<unknown>(`/api/players/${id}/stats`);
  }

  searchPlayers(query: string, limit?: number, offset?: number) {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (limit) params.set('limit', String(limit));
    if (offset) params.set('offset', String(offset));
    return this.get<unknown>(`/api/players?${params.toString()}`);
  }

  // Facilities
  getFacilities() {
    return this.get<unknown>('/api/facilities');
  }

  getFacility(id: number) {
    return this.get<unknown>(`/api/facilities/${id}`);
  }

  getFacilityResources(facilityId: number) {
    return this.get<unknown>(`/api/facilities/${facilityId}/resources`);
  }

  getFacilityImpact(facilityId: number) {
    return this.get<unknown>(`/api/facilities/${facilityId}/impact`);
  }

  // Projects
  getProjects() {
    return this.get<unknown>('/api/projects');
  }

  getProject(id: number) {
    return this.get<unknown>(`/api/projects/${id}`);
  }

  // Vehicles
  getVehicles() {
    return this.get<unknown>('/api/vehicles');
  }

  getVehicle(id: number) {
    return this.get<unknown>(`/api/vehicles/${id}`);
  }

  getVehicleStats() {
    return this.get<unknown>('/api/vehicles/stats/overview');
  }

  getVehicleHistory(id: number, limit?: number) {
    const params = limit ? `?limit=${limit}` : '';
    return this.get<unknown>(`/api/vehicles/${id}/history${params}`);
  }

  // Games
  getGameTypes() {
    return this.get<unknown>('/api/game/types');
  }

  getDailyGames() {
    return this.get<unknown>('/api/game/daily');
  }

  getLeaderboard(gameType: string, period?: string) {
    const params = period ? `?period=${period}` : '';
    return this.get<unknown>(`/api/game/leaderboard/${gameType}${params}`);
  }

  getGlobalLeaderboard() {
    return this.get<unknown>('/api/game/leaderboard');
  }

  submitScore(playerId: number, gameType: string, score: number) {
    return this.post('/api/game/submit', { playerId, gameType, score });
  }

  getPlayerScores(playerId: number) {
    return this.get<unknown>(`/api/game/scores/${playerId}`);
  }

  getGameStats(gameType: string) {
    return this.get<unknown>(`/api/game/stats/${gameType}`);
  }

  // Simulation
  getSimState() {
    return this.get<unknown>('/api/vehicles/simulation/state');
  }

  triggerSimTick() {
    return this.post('/api/vehicles/simulation/tick');
  }

  // Admin (requires admin key)
  adminQuery(sql: string, adminKey: string, params?: unknown[]) {
    return this.post('/api/admin/db/query', { sql, params }, {
      headers: { 'X-Admin-Key': adminKey },
    } as unknown as RequestConfig);
  }

  adminGetPlayers(limit?: number, offset?: number) {
    const params = new URLSearchParams();
    if (limit) params.set('limit', String(limit));
    if (offset) params.set('offset', String(offset));
    return this.get<unknown>(`/api/admin/players?${params.toString()}`);
  }

  adminGetMetrics() {
    return this.get<unknown>('/api/admin/metrics');
  }

  adminGetHealth() {
    return this.get<unknown>('/api/admin/health');
  }

  adminExport() {
    return this.get<unknown>('/api/admin/export');
  }
}

// ---- Singleton Export ----

export const api = new ApiClient();

// ---- React Query Integration ----

export function createQueryKey(base: string, ...params: (string | number | undefined)[]) {
  return [base, ...params.filter(Boolean)];
}

// ---- Error Handler ----

export function handleApiError(error: unknown): string {
  if ((error as { error?: string })?.error) {
    return (error as { error: string }).error;
  }
  if ((error as { message?: string })?.message) {
    return (error as { message: string }).message;
  }
  return 'An unexpected error occurred';
}

export function isAuthError(error: unknown): boolean {
  return (error as { status?: number })?.status === 401;
}

export function isOfflineError(error: unknown): boolean {
  return (error as { status?: number })?.status === 0 || (error as { error?: string })?.error?.includes('Offline') || false;
}
